// src/pages/usuarios/PermisosManagement.js - VERSIÓN CON TODOS LOS MÓDULOS
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import { MODULE_KEYS, MODULE_LABELS_ES } from '../../config/modulesCatalog';
import {
  ACCIONES_CRUD,
  CAJA_ACCIONES_LEGACY,
  COMPRAS_ACCIONES_EXTRA,
  PRODUCTOS_ACCIONES_EXTRA,
  PROVEEDORES_ACCIONES_EXTRA,
  REPORTES_ACCIONES_EXTRA,
  syncCajaPermisosBlock
} from '../../config/permisosCatalog';
import { useSafeUpdateFormGuard } from '../../hooks/useSafeUpdateFormGuard';

// Servicios
import usuariosService from '../../services/usuarios.service';

// Componentes
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';

// Iconos
import { 
  FaCheck, FaTimes, FaSave, FaEdit, FaUserCog
} from 'react-icons/fa';

const MODULO_ICONO_POR_ID = {
  productos: 'FaBox',
  categorias: 'FaTag',
  proveedores: 'FaTruck',
  punto_venta: 'FaCashRegister',
  compras: 'FaShoppingCart',
  ventas: 'FaShoppingBag',
  stock: 'FaWarehouse',
  reportes: 'FaChartBar',
  promociones: 'FaPercent',
  usuarios: 'FaUsers',
  sucursales: 'FaStore',
  clientes: 'FaUserTie',
  materias_primas: 'FaCubes',
  recetas: 'FaClipboardList',
  produccion: 'FaIndustry',
  vehiculos: 'FaCar',
  caja: 'FaCashRegister',
  gastos: 'FaMoneyBillWave',
  devoluciones: 'FaUndo',
  listas_precios: 'FaTags',
  transferencias: 'FaExchangeAlt',
  auditoria: 'FaHistory',
  configuracion: 'FaCog'
};

const MODULOS_SISTEMA = MODULE_KEYS.map((id) => ({
  id,
  nombre: MODULE_LABELS_ES[id] || id,
  icono: MODULO_ICONO_POR_ID[id] || 'FaCube'
}));

const EXTRA_ACTIONS_BY_MODULE = {
  caja: CAJA_ACCIONES_LEGACY,
  compras: COMPRAS_ACCIONES_EXTRA,
  productos: PRODUCTOS_ACCIONES_EXTRA,
  proveedores: PROVEEDORES_ACCIONES_EXTRA,
  reportes: REPORTES_ACCIONES_EXTRA
};

const accionesModulo = (moduloId) => [
  ...ACCIONES_CRUD,
  ...(EXTRA_ACTIONS_BY_MODULE[moduloId] || []).map(({ id }) => id)
];

const permisosVaciosModulo = (moduloId) => Object.fromEntries(
  accionesModulo(moduloId).map((accion) => [accion, false])
);

const normalizarPermisosModulo = (moduloId, permisosModulo = {}) => {
  const base = { ...permisosVaciosModulo(moduloId), ...permisosModulo };
  if (moduloId === 'reportes' && permisosModulo.ver_ganancias === undefined) {
    base.ver_ganancias = Boolean(base.ver);
  }
  return moduloId === 'caja' ? syncCajaPermisosBlock(base) : base;
};

/**
 * Plantillas de roles predefinidas
 */
const PLANTILLAS_ROLES = {
  vendedor: {
    nombre: 'Vendedor',
    permisos: {
      productos: { ver: true, crear: false, editar: false, eliminar: false },
      clientes: { ver: true, crear: true, editar: true, eliminar: false },
      ventas: { ver: true, crear: true, editar: false, eliminar: false },
      stock: { ver: true, crear: false, editar: false, eliminar: false },
      caja: { ver: true, crear: true, editar: false, eliminar: false, cerrar: true, ver_cierres: false, corregir_cierre: false }
    }
  },
  supervisor: {
    nombre: 'Supervisor',
    permisos: {
      productos: { ver: true, crear: true, editar: true, eliminar: false },
      categorias: { ver: true, crear: true, editar: true, eliminar: false },
      clientes: { ver: true, crear: true, editar: true, eliminar: true },
      ventas: { ver: true, crear: true, editar: true, eliminar: false },
      compras: { ver: true, crear: false, editar: false, eliminar: false },
      stock: { ver: true, crear: true, editar: true, eliminar: false },
      reportes: { ver: true, crear: false, editar: false, eliminar: false },
      caja: { ver: true, crear: true, editar: true, eliminar: false, cerrar: true, ver_cierres: true, corregir_cierre: true },
      devoluciones: { ver: true, crear: true, editar: true, eliminar: false }
    }
  },
  gerente: {
    nombre: 'Gerente',
    permisos: {
      // Todos los módulos con casi todos los permisos
      ...Object.fromEntries(
        MODULOS_SISTEMA.map(mod => [
          mod.id,
          { ver: true, crear: true, editar: true, eliminar: mod.id !== 'auditoria' }
        ])
      )
    }
  }
};

const PermisosManagement = () => {
  const { currentUser, syncEffectivePermissions } = useAuth();
  const [loading, setLoading] = useState(true);
  const [guardando, setGuardando] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState(null);
  const [permisos, setPermisos] = useState({});
  const [modoEdicion, setModoEdicion] = useState(false);
  useSafeUpdateFormGuard('usuarios-permisos', modoEdicion);

  useEffect(() => {
    cargarDatos();
  }, []);

  useEffect(() => {
    if (usuarioSeleccionado) {
      cargarPermisosUsuario(usuarioSeleccionado.id);
    }
  }, [usuarioSeleccionado]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const usuariosData = await usuariosService.obtenerTodos();
      setUsuarios(usuariosData);
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar usuarios');
    } finally {
      setLoading(false);
    }
  };

  const cargarPermisosUsuario = async (userId) => {
    try {
      // Por ahora usar los permisos del usuario seleccionado
      const permisosUsuario = usuarioSeleccionado.permisos || {};
      
      // Inicializar todos los módulos con permisos por defecto
      const permisosCompletos = {};
      MODULOS_SISTEMA.forEach(modulo => {
        permisosCompletos[modulo.id] = normalizarPermisosModulo(modulo.id, permisosUsuario[modulo.id]);
        if (modulo.id === 'reportes' && permisosCompletos.reportes.ver_ganancias === undefined) {
          permisosCompletos.reportes.ver_ganancias = Boolean(permisosCompletos.reportes.ver);
        }
      });
      
      setPermisos(permisosCompletos);
    } catch (error) {
      console.error('Error al cargar permisos:', error);
      toast.error('Error al cargar permisos del usuario');
    }
  };

  const handleTogglePermiso = (modulo, accion) => {
    if (!modoEdicion) return;
    
    setPermisos((prev) => {
      const nextMod = {
        ...prev[modulo],
        [accion]: !prev[modulo]?.[accion]
      };
      const next = { ...prev, [modulo]: modulo === 'caja' ? syncCajaPermisosBlock(nextMod) : nextMod };
      return next;
    });
  };

  const aplicarPlantilla = (plantillaKey) => {
    if (!modoEdicion) return;
    
    const plantilla = PLANTILLAS_ROLES[plantillaKey];
    if (!plantilla) return;
    
    // Aplicar permisos de la plantilla
    const nuevosPermisos = {};
    MODULOS_SISTEMA.forEach(modulo => {
      nuevosPermisos[modulo.id] = normalizarPermisosModulo(modulo.id, plantilla.permisos[modulo.id]);
    });
    
    setPermisos(nuevosPermisos);
    toast.success(`Plantilla "${plantilla.nombre}" aplicada`);
  };

  const guardarPermisos = async () => {
    if (!usuarioSeleccionado) return;
    
    try {
      setGuardando(true);
      
      // Actualizar permisos del usuario
      const permisosPayload = { ...permisos };
      if (permisosPayload.caja) {
        permisosPayload.caja = syncCajaPermisosBlock(permisosPayload.caja);
      }
      await usuariosService.actualizar(usuarioSeleccionado.id, {
        nombre: usuarioSeleccionado.nombre,
        email: usuarioSeleccionado.email,
        rol_id: usuarioSeleccionado.rol_id || usuarioSeleccionado.rol,
        permisos: permisosPayload
      });
      
      toast.success('Permisos guardados correctamente');
      setModoEdicion(false);

      if (usuarioSeleccionado.id === currentUser?.id) {
        await syncEffectivePermissions();
      }
      
      // Recargar usuarios para reflejar cambios
      await cargarDatos();
      
    } catch (error) {
      console.error('Error al guardar permisos:', error);
      toast.error('Error al guardar permisos');
    } finally {
      setGuardando(false);
    }
  };

  const marcarTodos = (modulo, valor) => {
    if (!modoEdicion) return;
    
    setPermisos(prev => {
      const nextMod = {
        ...(prev[modulo] || {}),
        ...Object.fromEntries(accionesModulo(modulo).map((accion) => [accion, valor]))
      };
      return {
        ...prev,
        [modulo]: modulo === 'caja' ? syncCajaPermisosBlock(nextMod) : nextMod
      };
    });
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-e0e3-foreground">
            Gestión de Permisos
          </h1>
          <p className="text-e0e3-muted text-sm mt-1">
            Configure permisos detallados por usuario
          </p>
        </div>
      </div>

      {/* Selector de usuario */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium">Seleccionar Usuario</h3>
          {usuarioSeleccionado && (
            <div className="flex gap-2">
              {!modoEdicion ? (
                <Button
                  size="sm"
                  color="primary"
                  onClick={() => setModoEdicion(true)}
                  icon={<FaEdit />}
                >
                  Editar Permisos
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    color="secondary"
                    onClick={() => {
                      setModoEdicion(false);
                      cargarPermisosUsuario(usuarioSeleccionado.id);
                    }}
                    icon={<FaTimes />}
                  >
                    Cancelar
                  </Button>
                  <Button
                    size="sm"
                    color="success"
                    onClick={guardarPermisos}
                    loading={guardando}
                    icon={<FaSave />}
                  >
                    Guardar Cambios
                  </Button>
                </>
              )}
            </div>
          )}
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-3">
          {usuarios.map(usuario => (
            <button
              key={usuario.id}
              onClick={() => setUsuarioSeleccionado(usuario)}
              className={`p-3 border rounded-lg text-left transition-colors ${
                usuarioSeleccionado?.id === usuario.id
                  ? 'border-indigo-500 bg-indigo-50'
                  : 'border-white/10 hover:border-white/20'
              }`}
            >
              <div className="font-medium text-sm">
                {usuario.nombre} {usuario.apellido}
              </div>
              <div className="text-xs text-e0e3-muted">{usuario.email}</div>
              <div className="text-xs text-e0e3-accent mt-1">
			  {typeof usuario.rol === 'object' ? usuario.rol.nombre : usuario.rol}
			</div>
            </button>
          ))}
        </div>
      </Card>

      {/* Tabla de permisos */}
      {usuarioSeleccionado && (
        <>
          {/* Plantillas rápidas */}
          {modoEdicion && (
            <Card>
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-medium text-e0e3-muted">Aplicar Plantilla Rápida:</h3>
                <div className="flex gap-2">
                  {Object.entries(PLANTILLAS_ROLES).map(([key, plantilla]) => (
                    <Button
                      key={key}
                      size="sm"
                      color="secondary"
                      onClick={() => aplicarPlantilla(key)}
                      icon={<FaUserCog />}
                    >
                      {plantilla.nombre}
                    </Button>
                  ))}
                </div>
              </div>
            </Card>
          )}

          <Card>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-white/10">
                <thead className="bg-white/[0.04]">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-e0e3-muted uppercase tracking-wider">
                      Módulo
                    </th>
                    {ACCIONES_CRUD.map(accion => (
                      <th key={accion} className="px-6 py-3 text-center text-xs font-medium text-e0e3-muted uppercase tracking-wider">
                        {accion}
                      </th>
                    ))}
                    <th className="px-6 py-3 text-left text-xs font-medium text-e0e3-muted uppercase tracking-wider">
                      Permisos granulares
                    </th>
                    {modoEdicion && (
                      <th className="px-6 py-3 text-center text-xs font-medium text-e0e3-muted uppercase tracking-wider">
                        Acciones
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/10">
                  {MODULOS_SISTEMA.map(modulo => (
                    <tr key={modulo.id} className="hover:bg-white/[0.06]">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-e0e3-foreground">
                          {modulo.nombre}
                        </div>
                      </td>
                      {ACCIONES_CRUD.map(accion => (
                        <td key={accion} className="px-6 py-4 whitespace-nowrap text-center">
                          <button
                            type="button"
                            onClick={() => handleTogglePermiso(modulo.id, accion)}
                            disabled={!modoEdicion}
                            className={`${
                              modoEdicion ? 'cursor-pointer' : 'cursor-not-allowed'
                            }`}
                          >
                            {permisos[modulo.id]?.[accion] ? (
                              <FaCheck className="text-emerald-400 mx-auto" />
                            ) : (
                              <FaTimes className="text-red-400 mx-auto" />
                            )}
                          </button>
                        </td>
                      ))}
                      <td className="px-6 py-4 min-w-[260px]">
                        {(EXTRA_ACTIONS_BY_MODULE[modulo.id] || []).length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {(EXTRA_ACTIONS_BY_MODULE[modulo.id] || []).map(({ id: accion, label }) => (
                              <button
                                key={`${modulo.id}-${accion}`}
                                type="button"
                                onClick={() => handleTogglePermiso(modulo.id, accion)}
                                disabled={!modoEdicion}
                                title={label}
                                className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs ${
                                  permisos[modulo.id]?.[accion]
                                    ? 'border-emerald-400/40 bg-emerald-400/10 text-emerald-300'
                                    : 'border-white/10 bg-white/[0.03] text-e0e3-muted'
                                } ${modoEdicion ? 'cursor-pointer hover:border-white/30' : 'cursor-not-allowed'}`}
                              >
                                {permisos[modulo.id]?.[accion] ? <FaCheck /> : <FaTimes />}
                                <span>{label}</span>
                              </button>
                            ))}
                          </div>
                        ) : (
                          <span className="text-e0e3-muted/60">Sin permisos granulares</span>
                        )}
                      </td>
                      {[].map(({ id: accion, label }) => (
                        <td key={`${modulo.id}-${accion}`} className="px-4 py-4 whitespace-nowrap text-center" title={label}>
                          {modulo.id === 'caja' ? (
                            <button
                              type="button"
                              onClick={() => handleTogglePermiso('caja', accion)}
                              disabled={!modoEdicion}
                              className={modoEdicion ? 'cursor-pointer' : 'cursor-not-allowed'}
                            >
                              {permisos.caja?.[accion] ? (
                                <FaCheck className="text-emerald-400 mx-auto" />
                              ) : (
                                <FaTimes className="text-red-400 mx-auto" />
                              )}
                            </button>
                          ) : (
                            <span className="text-e0e3-muted/60">—</span>
                          )}
                        </td>
                      ))}
                      {[].map(({ id: accion, label }) => (
                        <td key={`${modulo.id}-${accion}`} className="px-4 py-4 whitespace-nowrap text-center" title={label}>
                          {modulo.id === 'reportes' ? (
                            <button
                              type="button"
                              onClick={() => handleTogglePermiso('reportes', accion)}
                              disabled={!modoEdicion}
                              className={modoEdicion ? 'cursor-pointer' : 'cursor-not-allowed'}
                            >
                              {permisos.reportes?.[accion] ? (
                                <FaCheck className="text-emerald-400 mx-auto" />
                              ) : (
                                <FaTimes className="text-red-400 mx-auto" />
                              )}
                            </button>
                          ) : (
                            <span className="text-e0e3-muted/60">—</span>
                          )}
                        </td>
                      ))}
                      {modoEdicion && (
                        <td className="px-6 py-4 whitespace-nowrap text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => marcarTodos(modulo.id, true)}
                              className="text-xs text-emerald-400 hover:text-emerald-300"
                              title="Marcar todos"
                            >
                              Todos
                            </button>
                            <span className="text-e0e3-muted/60">|</span>
                            <button
                              onClick={() => marcarTodos(modulo.id, false)}
                              className="text-xs text-red-400 hover:text-red-300"
                              title="Desmarcar todos"
                            >
                              Ninguno
                            </button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default PermisosManagement;
